import React, { useMemo, useState } from "react";
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowRight } from "@material-ui/icons";
import ScrapeExpandedRow from "./ScrapeExpandedRow";
import { ScrapeConfig } from "../../Types/Scrapes";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12)",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 6,
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,0.2)",
      borderRadius: 3,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    position: "sticky" as const,
    top: 0,
    zIndex: 1,
  },
  row: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(86,109,179,0.04)",
    },
  },
  rowOdd: {
    cursor: "pointer",
    backgroundColor: "#f9f9f9",
    "&:hover": {
      backgroundColor: "rgba(86,109,179,0.04)",
    },
  },
  expandedCell: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  atsSummary: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  clientId: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontFamily: "monospace",
  },
}));

interface ClientGroup {
  clientId: string;
  clientName: string;
  configs: ScrapeConfig[];
}

interface ScrapeTableProps {
  configs: ScrapeConfig[];
  isLoading: boolean;
  style?: React.CSSProperties;
}

function buildAtsSummary(configs: ScrapeConfig[]): string {
  const counts: Record<string, number> = {};
  for (const c of configs) {
    const name = c.atsName || "Unknown";
    counts[name] = (counts[name] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([name, count]) => `${name} (${count})`)
    .join(", ");
}

export default function ScrapeTable({ configs, isLoading, style }: ScrapeTableProps) {
  const classes = useStyles();
  const [expandedClientId, setExpandedClientId] = useState<string | null>(null);

  const clientGroups = useMemo<ClientGroup[]>(() => {
    const map = new Map<string, ClientGroup>();
    for (const config of configs) {
      const existing = map.get(config.clientId);
      if (existing) {
        existing.configs.push(config);
      } else {
        map.set(config.clientId, {
          clientId: config.clientId,
          clientName: config.clientName || config.clientId,
          configs: [config],
        });
      }
    }
    return Array.from(map.values());
  }, [configs]);

  const handleRowClick = (clientId: string) => {
    setExpandedClientId(expandedClientId === clientId ? null : clientId);
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer} style={style}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell} style={{ width: 48 }} />
            <TableCell className={classes.headerCell}>Client</TableCell>
            <TableCell className={classes.headerCell}>Scrapes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                Loading...
              </TableCell>
            </TableRow>
          ) : clientGroups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                No scrape configurations found
              </TableCell>
            </TableRow>
          ) : (
            clientGroups.map((group, index) => (
              <React.Fragment key={group.clientId}>
                <TableRow
                  className={index % 2 === 0 ? classes.row : classes.rowOdd}
                  onClick={() => handleRowClick(group.clientId)}
                >
                  <TableCell>
                    <IconButton size="small">
                      {expandedClientId === group.clientId ? (
                        <KeyboardArrowDown />
                      ) : (
                        <KeyboardArrowRight />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {group.clientName}
                    </Typography>
                    <span className={classes.clientId}>
                      Client ID: {group.clientId}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.atsSummary}>
                      {buildAtsSummary(group.configs)}
                    </span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.expandedCell} colSpan={3}>
                    <Collapse
                      in={expandedClientId === group.clientId}
                      timeout="auto"
                      unmountOnExit
                    >
                      <ScrapeExpandedRow configs={group.configs} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
